import { useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { catalogApi } from '../api/catalogApi';
import { progressApi } from '../api/progressApi';
import { useApi, useAsync } from '../hooks/useApi';
import { useSettings } from '../settings/SettingsContext';
import { Loading, LoadError } from '../components/Loading';
import Drill from '../study/Drill';
import { validateFieldModes } from '../../../shared/api';
import type { CardResult } from '../../../shared/types';

/**
 * One section's deck.
 *
 * Loads, then hands off to `<Drill>`. The split matters: a React state
 * initializer runs on the component's *first* render, and on that render the
 * section is still loading — so a session hook mounted here would deal an empty
 * deck, be `done` immediately, and drop the learner straight onto the summary
 * screen at 0%. Guarding with an early return does not help, because hooks run
 * before the return.
 *
 * Keeping the drill in a child that is only mounted once the cards exist makes
 * that impossible by construction rather than by remembering to check.
 */
export default function FlashcardsPage() {
  const { sectionId = '' } = useParams();
  const api = useApi();
  const { settings } = useSettings();

  const section = useAsync(() => catalogApi.section(sectionId, api), [sectionId, api]);
  const language = useAsync(
    () => (section.data ? catalogApi.language(section.data.languageCode, api) : Promise.resolve(null)),
    [section.data?.languageCode, api],
  );

  // Identity-stable so the drill's post-once effect does not re-fire.
  const record = useCallback(
    (results: { cardId: string; result: CardResult }[]) =>
      progressApi.recordSession(sectionId, results, api),
    [sectionId, api],
  );

  if (section.loading || language.loading) {
    return (
      <main className="page">
        <Loading what="the deck" />
      </main>
    );
  }

  if (section.error || !section.data || !language.data) {
    return (
      <main className="page">
        <LoadError message={section.error ?? 'Section not found'} onRetry={section.reload} />
      </main>
    );
  }

  const data = section.data;
  const lang = language.data;
  const backTo = `/sections/${data.id}?tab=exercises`;

  // An empty deck is checked before anything else: there is no drill to run and
  // no session to summarize, and the "finished" screen would be a lie.
  if (data.cards.length === 0) {
    return (
      <main className="page">
        <p className="muted state-message">This section has no cards yet.</p>
        <Link to={backTo} className="btn">
          Back to the section
        </Link>
      </main>
    );
  }

  const problem = validateFieldModes(settings.flashcards.fields, lang);
  if (problem) {
    return (
      <main className="page">
        <p className="error">{problem}</p>
        <Link to={backTo} className="btn">
          Back to the card setup
        </Link>
      </main>
    );
  }

  // The section that follows this one in the chapter, or null at the end of it.
  // `siblings` rides along on the section response, so finishing a drill can
  // offer the next section without a second request.
  const siblingIndex = data.siblings.findIndex((s) => s.id === data.id);
  const nextSection =
    siblingIndex >= 0 && siblingIndex < data.siblings.length - 1
      ? data.siblings[siblingIndex + 1]
      : null;

  return (
    <Drill
      cards={data.cards}
      language={lang}
      settings={settings}
      label={`${data.number} ${data.title}`}
      exitTo={backTo}
      exitLabel="Back to the section"
      deckNoun="section"
      sectionId={sectionId}
      onRecord={record}
      summaryActions={(finished) =>
        nextSection && (
          // Offered but not pushed: cards still unseen here, or words just
          // missed, are both worth more than moving on.
          <Link
            to={`/sections/${nextSection.id}`}
            className={finished ? 'btn btn--primary' : 'btn'}
          >
            Next: {nextSection.number} {nextSection.title}
          </Link>
        )
      }
    />
  );
}
